import { FormStatus } from '../../../enum/form-status.enum';
import { Component } from '@angular/core';
import { BaseFormTableComponent } from '../base/base-form-table.component';

/**
 * 查詢頁面的抽象類別，繼承 BaseFormTableComponent。
 * 設定沒有 MasterMenuBar 並直接進入編輯狀態。
 * 另外增加了查詢的抽象 method。
 *
 * @export
 * @abstract
 * @class SimpleSearchComponent
 * @extends {BaseFormTableComponent}
 */
@Component({
  template: ''
})
export abstract class SimpleSearchComponent extends BaseFormTableComponent {

  /**
   * 查詢畫面直接就是編輯狀態，不需要有 MenuBar。
   */
  override hasMasterMenuBar: boolean = false;

  /**
   * 查詢畫面直接就是編輯狀態。
   */
  override masterFormStatus: FormStatus = FormStatus.EDIT;

  /**
   * 查詢畫面直接就是編輯狀態。
   */
  override detailFormStatus: FormStatus = FormStatus.EDIT;

  /**
   * 不管有沒有提供批次刪除按鈕，先把成功要顯示訊息設定為 true。
   */
  override showBatchDeleteSuccessMessage: boolean = true;

  /**
   * 控制查詢條件輸入的 panel 是否縮起。
   */
  panelCollapsed: boolean = false;

  /**
   * 重新查詢的 Condition 物件。
   * 每次查詢後都應該把它紀錄下來，當需要重新查詢的時候要直接拿來使用。
   */
  requeryCondition: any;

  /**
   * 查詢 API 成功回傳後，是否要顯示沒有符合條件的資料訊息。
   * 預設顯示，如果有要特別處理的 override 這個設成 false;
   */
  showNoDataFoundMessage: boolean = true;

  /**
   * 查詢畫面直接就是編輯狀態，不需要有 MenuBar。
   * 所以相關的 function 都實作 return。
   */
  override async doMasterMenuSubmit(): Promise<boolean> {
    return true;
  }

  /**
   * 查詢畫面直接就是編輯狀態，不需要有 MenuBar。
   * 所以相關的 function 都實作 return。
   */
  override async doMasterMenuDelete(): Promise<boolean> {
    return true;
  }

  /**
   * 查詢畫面直接就是編輯狀態，不需要有 MenuBar。
   * 所以相關的 function 都實作 return。
   */
  override async beforeMasterMenuDiscard(): Promise<boolean> {
    return true;
  }

  /**
   * 查詢畫面直接就是編輯狀態，不需要有 MenuBar。
   * 所以相關的 function 都實作 return。
   */
  override async beforeMasterMenuSubmit(): Promise<boolean> {
    return true;
  }

  /**
   * 查詢畫面直接就是編輯狀態，不需要有 MenuBar。
   * 所以相關的 function 都實作 return。
   */
  override initExtraMasterMenu(): void {
    return;
  }

  /**
   * 查詢畫面直接就是編輯狀態，不需要有 MenuBar。
   * 所以相關的 function 都實作 return。
   */
  override setExtraMasterMenuStatus(): void {
    return;
  }

  /**
   * 查詢畫面不會改變 Detail 狀態。
   * 所以實作 return。
   */
  override afterDetailMenuStatusChange(): void {
    return;
  }

  /**
   * 需要在查詢前做的事情，可以在這做一些額外的驗證。
   * 如果有做額外的驗證要配合 formGroup 的 valid，不然驗證到有錯還是會繼續執行查詢。
   *
   * 如果沒有特別要做的事情，實作後直接 return 就可以。
   */
  abstract beforeQuery(): Promise<boolean>;

  /**
   * 按下查詢按鈕要做的動作。
   * (1) 呼叫 beforeQuery() 做事前的處理。
   * (2) 進行欄位驗證。
   * (3) 欄位驗證過後，顯示處理中遮罩。
   * (4) 清除 Table 多選選擇的資料。
   * (5) 確定所有 autoAomplete 欄位都做完驗證才呼叫 doQuery() 進行查詢。
   * (6) 處理完後呼叫 doQueryCallback()。
   */
  async query(): Promise<void> {
    // console.log('query before valid - waitComplete = ' + this.waitComplete);
    this.loadingMaskService.show();
    const canQuery = await this.beforeQuery();

    if (!canQuery) {
      this.loadingMaskService.hide();
      return;
    }

    this.panelCollapsed = false;
    this.submitted = true;
    if (this.formGroup.valid) {
      // 清除已選擇 Row
      this.clearRowSelectedDatas();

      this.waitAutoCompleteCompleted(
        async() => {
          // complete 後要實際去執行 doQuery()
          const queried = await this.doQuery();
          if (queried) {
            this.afterQuery();
          }
        }
      );
    } else {
      this.loadingMaskService.hide();
    }
  }

  /**
   * 重新查詢。
   * 通常會用在查詢出來的資料有異動的時候呼叫，用前一次查詢的條件重新查詢。
   */
  async requery(): Promise<boolean> {
    this.loadingMaskService.show();
    // 清除已選擇 Row
    this.clearRowSelectedDatas();
    const queried = await this.doQuery(this.requeryCondition);
    if (queried) {
      this.afterQuery();
    }
    return queried;
  }

  /**
   * 要自己實作實際查詢做的事情。
   * 欄位驗證成功後也要把查詢條件寫到 requeryCondition 內，供之後重新查詢使用。
   * @param callback 成功後要呼叫的 callback Function
   */
  abstract doQuery(requeryCondition?: any): Promise<boolean>;

  /**
   * 查詢完畢後要做的事情。
   * (1) 移除處理中遮罩。
   */
  afterQuery(): void {
    console.log('BaseSearchComponent afterQuery');

    if (this.showNoDataFoundMessage && this.detailDatas.length == 0) {
      this.systemMessageService.success('message.detail.no-data-found');
    }

    // 切回第一頁
    // setTimeout 0 是重點，不然頁數顯示有可能會停在上一次的查詢結果
    setTimeout(() => {
      this.tableComponent.first = 0;
    }, 0);
    this.submitted = false;

    setTimeout(() => {
      this.setDetailMenuStatus();
      this.loadingMaskService.hide();
    }, 1000);
  }

  /**
   * 清除 searchForm，還原原始狀態。
   */
  searchReset(): void {
    // 清空 Detail 資料
    this.clearDetail();
    this.formGroup.reset();
    this.formGroup.patchValue(this.originalFormRawValue);
    this.submitted = false;
  }

  /**
   * 清空 Detail 資料
   */
  clearDetail(): void {
    // 清除已選擇 Row
    this.clearRowSelectedDatas();

    // 清除查詢結果
    this.detailDatas = [];

    // 重繪 Table 功能列
    this.setDetailMenuStatus();
  }

  /**
   * 為了避免影響既有程式，預設不提供批次刪除
   * 有需要的再自己 override
   */
  override async doDetailMenuBatchDelete(): Promise<boolean> {
    return true;
  }
}
